import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, LightBulbIcon } from './Icons';
import { GUIDE_CONFIG } from '../../config';
import GuideVideoSection, { hasGuideVideoOrContent } from './GuideVideoSection';
import { FAB_BOTTOM_GUIDE, FAB_Z_OVERLAY, fabPortalFixedStyle } from '../../constants/fabDock';

export interface HelpStep {
    title: string;
    description: string;
    icon?: React.ReactNode;
}

interface HelpOnboardingProps {
    steps: HelpStep[];
    role: 'teacher' | 'parent' | 'student';
}

const HelpOnboarding: React.FC<HelpOnboardingProps> = ({ steps, role }) => {
    const [mounted, setMounted] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);

    useEffect(() => {
        setMounted(true);
    }, []);

    const roleBarClass = 'bg-teal-600 hover:bg-teal-700';
    const roleAccentText = 'text-teal-600';

    const roleLabel = {
        teacher: 'Giáo viên',
        parent: 'Phụ huynh',
        student: 'Học sinh'
    };

    const showGuideSlide = hasGuideVideoOrContent();
    const totalSteps = steps.length + (showGuideSlide ? 1 : 0);
    const isGuideSlide = showGuideSlide && currentStep === steps.length;
    const featureStep = !isGuideSlide ? steps[currentStep] : undefined;

    const guideTitle = GUIDE_CONFIG.TITLE || 'Hướng dẫn sử dụng';

    const trigger = (
        <button
            type="button"
            onClick={() => { setCurrentStep(0); setIsOpen(true); }}
            className={`h-14 w-14 sm:min-w-14 sm:w-auto sm:px-3 rounded-2xl shadow-2xl shadow-teal-900/20 ${roleBarClass} text-white active:scale-95 transition-all group flex items-center justify-center gap-2 touch-none overflow-visible ring-2 ring-white/20`}
            style={fabPortalFixedStyle(FAB_BOTTOM_GUIDE)}
            title="Hướng dẫn sử dụng"
            aria-label="Mở hướng dẫn sử dụng"
        >
            <i className="ti ti-bulb text-2xl leading-none text-white drop-shadow-sm pointer-events-none" aria-hidden />
            <span className="hidden sm:inline max-w-0 overflow-hidden opacity-0 group-hover:max-w-[11rem] group-hover:opacity-100 group-hover:ml-1 transition-all duration-300 whitespace-nowrap font-black uppercase tracking-widest text-[10px] text-white pointer-events-none">
                Hướng dẫn
            </span>
        </button>
    );

    const modal = (
        <div
            className="flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: FAB_Z_OVERLAY,
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="help-onboarding-title"
        >
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
                <div className={`p-6 shrink-0 ${roleBarClass} text-white flex justify-between items-center`}>
                    <div className="flex items-center gap-2 min-w-0">
                        <i className="ti ti-bulb text-xl leading-none shrink-0" aria-hidden />
                        <h3 id="help-onboarding-title" className="font-black uppercase tracking-widest text-sm leading-tight">
                            Hướng dẫn sử dụng - {roleLabel[role]}
                        </h3>
                    </div>
                    <button
                        type="button"
                        onClick={() => { setIsOpen(false); setCurrentStep(0); }}
                        className="p-1 hover:bg-white/20 rounded-full transition-colors shrink-0"
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                <div className="p-6 sm:p-10 flex flex-col flex-1 min-h-0 text-center">
                    <div className={`flex-1 min-h-0 overflow-y-auto overscroll-contain ${isGuideSlide ? 'text-left' : ''}`}>
                        {isGuideSlide ? (
                            <div className="max-w-xl mx-auto w-full pb-4">
                                <h4 className="text-xl sm:text-2xl font-black text-slate-800 mb-6 text-center">
                                    {guideTitle}
                                </h4>
                                <GuideVideoSection title={`${guideTitle} — ${roleLabel[role]}`} />
                            </div>
                        ) : (
                            <>
                                <div className="w-24 h-24 bg-slate-50 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-inner">
                                    {featureStep?.icon || <LightBulbIcon className={`h-12 w-12 ${roleAccentText}`} />}
                                </div>
                                <h4 className="text-2xl font-black text-slate-800 mb-4">{featureStep?.title}</h4>
                                <p className="text-slate-500 leading-relaxed font-medium mb-4">{featureStep?.description}</p>
                            </>
                        )}
                    </div>

                    <div className="flex justify-between items-center mt-6 pt-6 border-t border-slate-100 shrink-0">
                        <div className="flex gap-1.5 flex-wrap">
                            {Array.from({ length: totalSteps }, (_, idx) => (
                                <div
                                    key={idx}
                                    className={`h-1.5 rounded-full transition-all ${idx === currentStep ? `w-8 ${roleBarClass}` : 'w-1.5 bg-slate-200'}`}
                                />
                            ))}
                        </div>

                        <div className="flex gap-3 shrink-0">
                            {currentStep > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep((prev) => prev - 1)}
                                    className="px-4 py-2 text-slate-400 font-black text-xs uppercase tracking-widest hover:text-slate-600 transition-colors"
                                >
                                    Quay lại
                                </button>
                            )}

                            {currentStep < totalSteps - 1 ? (
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep((prev) => prev + 1)}
                                    className={`px-6 py-2 ${roleBarClass} text-white font-black text-xs uppercase tracking-widest rounded-xl hover:opacity-90 transition-all shadow-lg active:scale-95`}
                                >
                                    Tiếp theo
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => { setIsOpen(false); setCurrentStep(0); }}
                                    className={`px-6 py-2 ${roleBarClass} text-white font-black text-xs uppercase tracking-widest rounded-xl hover:opacity-90 transition-all shadow-lg active:scale-95`}
                                >
                                    Bắt đầu ngay
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    if (!mounted || typeof document === 'undefined') return null;
    if (!isOpen) return createPortal(trigger, document.body);
    return createPortal(modal, document.body);
};

export default HelpOnboarding;
